<template>
  <div class="sb_page_groepslessen">
    <Countdown
      v-if="!loading && lessons.length"
      :lessons="lessonSet"
      :lesson-interval="lessonIntervalInSeconds"
      :current-time="now"
      :current-set="currentSet"
      :max-set="maxSet"
      occupancy-format="relative"
    >
    </Countdown>
  </div>
</template>

<script>
import {
  isAfter,
  isSameDay,
  addDays,
  differenceInMinutes,
  addMinutes,
  startOfDay,
} from "date-fns";

import { getGroupLessons, getLessons, toGymTime } from "@/lib/backend";

import Countdown from "@/components/Countdown";

const MAX_SET_SIZE = 6;
const PFG_LAST_SET_LOCAL_STORAGE_KEY = "pfg_last_set";

function calculatePercentage(occupied, total) {
  return Math.round(occupied / total * 100);
}

export default {
  components: {
    Countdown,
  },
  computed: {
    lessonSet() {
      if (this.lessons.length <= MAX_SET_SIZE) {
        return this.lessons;
      }
      return this.lessons.slice(
        0 + (this.currentSet - 1) * MAX_SET_SIZE,
        this.currentSet * MAX_SET_SIZE
      );
    },
    lessons() {
      if (this.startingLessons.length) {
        return this.startingLessons;
      }
      if (this.todaysLessons.length) {
        return this.todaysLessons;
      }
      if (this.lessonsTomorrowOrDayAfter.length) {
        return this.lessonsTomorrowOrDayAfter;
      }
      return [];
    },
    futureLessons() {
      return this.allLessons.filter(({ datum }) => isAfter(datum, this.now));
    },
    todaysLessons() {
      return this.futureLessons.filter((lesson) => {
        return isSameDay(this.now, lesson.datum);
      });
    },
    startingLessons() {
      return this.todaysLessons
        .filter((lesson) => {
          const minutesUntilLessonStarts = differenceInMinutes(
            lesson.datum,
            this.now
          );
          return minutesUntilLessonStarts >= 0 && minutesUntilLessonStarts < 10;
        })
        .map((lesson) => ({
          ...lesson,
          isStarting: true,
        }));
    },
    lessonsTomorrowOrDayAfter() {
      const filterLessonsOnDay = (day) => (lesson) => {
        return isSameDay(day, lesson.datum);
      };
      const tomorrowsLessons = this.futureLessons.filter(
        filterLessonsOnDay(addDays(this.now, 1))
      );
      if (tomorrowsLessons.length) {
        return tomorrowsLessons;
      }
      return this.futureLessons.filter(
        filterLessonsOnDay(addDays(this.now, 2))
      );
    },
    lessonIntervalInSeconds() {
      return this.totalTime / this.lessonSet.length;
    },
    currentSet() {
      if (this.loading) {
        return;
      }
      const lastSet =
        +localStorage.getItem(PFG_LAST_SET_LOCAL_STORAGE_KEY) || 0;
      const nextSet = lastSet + 1;

      if (
        !this.lessons.length ||
        nextSet > Math.ceil(this.lessons.length / MAX_SET_SIZE)
      ) {
        localStorage.setItem(PFG_LAST_SET_LOCAL_STORAGE_KEY, 1);
        return 1;
      }

      localStorage.setItem(PFG_LAST_SET_LOCAL_STORAGE_KEY, nextSet);
      return nextSet;
    },
    maxSet() {
      return Math.ceil(this.lessons.length / MAX_SET_SIZE);
    }
  },
  data() {
    const TOTAL_TIME_MILLISECONDS = (process.env.NODE_ENV === 'development' ? 30 : 60) * 1000;
    return {
      loading: false,
      vestigingId: +this.$route.query.vestiging,
      allLessons: [],
      now: toGymTime(new Date()),
      totalTime: TOTAL_TIME_MILLISECONDS
    };
  },
  async mounted() {
    this.loading = true;
    const [groupLessons, lessons] = await Promise.all([
      getGroupLessons(),
      getLessons({
        gymId: this.vestigingId,
        from: this.now,
        // The screen shows today's remaining lessons, or the first upcoming
        // day within the next two days.
        until: addDays(startOfDay(this.now), 3),
      }),
    ]);
    this.allLessons = this.getSortedLessons(lessons, groupLessons);
    this.loading = false;
  },
  methods: {
    getSortedLessons(lessons, groupLessons) {
      const defaultCharacteristics = [
        {
          attribuut: 'intensity',
          waarde: 0
        },
        {
          attribuut: 'strength',
          waarde: 0
        },
        {
          attribuut: 'endurance',
          waarde: 0
        },
        {
          attribuut: 'mobility',
          waarde: 0
        },
        {
          attribuut: 'flexibility',
          waarde: 0
        },
      ]

      return (lessons
          // Map each lesson to the shape the countdown consumes, joined with
          // its group lesson meta data.
          .map((lesson) => {
            const meta = groupLessons.find(
              (groupLesson) =>
                groupLesson.id === lesson.groupLessonId ||
                groupLesson.sourceId === lesson.groupLessonId
            );
            if (!meta) {
              return null;
            }
            const startDate = lesson.date;
            const endDate = addMinutes(startDate, lesson.duration);
            const image = meta.images.find(
              ({ format }) => format === "2048x2048"
            );
            const kenmerken = meta.characteristics.length
              ? meta.characteristics.map(({ attribute, value }) => ({
                  attribuut: attribute,
                  waarde: value || 0,
                }))
              : defaultCharacteristics;
            return {
              id: lesson.id,
              itemType: lesson.type === "outside" ? "buiten" : lesson.type,
              meta: { ...meta, naam: meta.name, kenmerken },
              kenmerken,
              datum: startDate,
              zaal: { id: lesson.room.id, naam: lesson.room.name },
              plekken: lesson.totalSpots,
              bezet: lesson.occupiedSpots,
              remaining: lesson.totalSpots,
              percentage: calculatePercentage(
                lesson.occupiedSpots,
                lesson.totalSpots
              ),
              startDate,
              endDate,
              image: (image && image.url) || "",
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.datum - b.datum))
    },
  },
};
</script>

<style lang="scss" scoped>
.sb_page_groepslessen {
  width: 100%;
  height: 100%;
  background: url("~@/assets/bg-color-3.jpg");
  background-size: cover;
  background-position: 50% 50%;
  display: flex;
  flex-direction: column;
}
</style>